import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { FEATURE_LINKS } from "../pages/features/links";
import { SOLUTION_LINKS } from "../pages/solutions/links";

// Mobile nav for logged-out visitors. The Features / For Employers mega-menus are
// desktop-only, so on phones this hamburger surfaces the same pages (from the shared
// link lists) plus Log in / Get started. Hidden at ≥ sm where the desktop nav shows.

function Item({ f, onNavigate }) {
  const Icon = f.icon;
  if (f.comingSoon) {
    return (
      <div className="flex items-start gap-3 rounded-xl p-3 opacity-70 select-none" title="Coming soon">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-secondary text-muted-foreground">
          <Icon className="w-[18px] h-[18px]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-muted-foreground">{f.label}</span>
            <span className="font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: `${f.accent}1A`, color: f.accent }}>Soon</span>
          </div>
          <div className="text-xs text-muted-foreground/70 leading-snug">{f.desc}</div>
        </div>
      </div>
    );
  }
  return (
    <Link to={f.to} onClick={onNavigate} className="flex items-start gap-3 rounded-xl p-3 active:bg-secondary transition-colors">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${f.accent}14`, color: f.accent }}>
        <Icon className="w-[18px] h-[18px]" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[#0F172A]">{f.label}</div>
        <div className="text-xs text-muted-foreground leading-snug">{f.desc}</div>
      </div>
    </Link>
  );
}

export default function MobileMenu() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="sm:hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        className="p-2 -mr-2 text-[#0F172A] hover:text-[#0025D8] transition-colors"
        data-testid="nav-mobile-toggle"
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Rendered via portal into <body> so the fixed panel isn't trapped by the
          header's backdrop-filter containing block (which collapses fixed children). */}
      {open && createPortal(
        <>
          <div className="fixed inset-0 top-16 bg-black/20 z-[45]" onClick={close} />
          <div className="fixed inset-x-0 top-16 bottom-0 z-[46] bg-white overflow-y-auto overscroll-contain">
            <div className="px-5 py-5 space-y-6">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-3 mb-1">Features</div>
                <div className="space-y-0.5">
                  {FEATURE_LINKS.map((f) => <Item key={f.key} f={f} onNavigate={close} />)}
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-3 mb-1">For Employers</div>
                <div className="space-y-0.5">
                  {SOLUTION_LINKS.map((f) => <Item key={f.key} f={f} onNavigate={close} />)}
                </div>
              </div>

              <div className="border-t border-border pt-5 space-y-2.5">
                <Link to="/login" onClick={close}
                  className="block text-center text-sm font-semibold text-[#0F172A] border border-border rounded-xl py-3 active:bg-secondary transition-colors">
                  Log in
                </Link>
                <Link to="/signup" onClick={close}
                  className="flex items-center justify-center gap-2 text-sm font-semibold text-white bg-[#0025D8] active:bg-[#001CB3] rounded-xl py-3 transition-colors">
                  Get started <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/book-demo" onClick={close}
                  className="block text-center text-xs font-semibold text-[#0025D8] py-1.5">
                  Book a demo →
                </Link>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
